import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, CreditCard, Keyboard, RefreshCw, Search } from 'lucide-react'
import {
  subscribersApi,
  type SubscriberDecision,
  type SubscriberFlag,
  type SubscriberRow,
} from '@/api/subscribers'
import { Button } from '@/components/ui'
import SubscriberPanel, { Chip, useSaveDecision } from './SubscriberPanel'
import {
  DECISION_LABEL,
  DECISION_OPTIONS,
  FLAG_LABEL,
  FLAG_ORDER,
  FLAG_TONE,
  STATUS_TONE,
  TONE_CLASS,
  attentionScore,
  ddmmyyyy,
  ddmmyyyyTime,
  isPayingActive,
  moneyBreakdown,
  nis,
  periodLabel,
  sortedFlags,
  subscriberFlagLabel,
  subscriberFlagTone,
  keyDate,
  daysUntil,
  withDerivedFlags,
} from './subscribersFormat'

type SortKey = 'attention' | 'name' | 'monthly' | 'open' | 'next'
type FlagFilter = SubscriberFlag | 'all' | 'ok' | 'paying' | 'trial_missing_card'

interface ViewState {
  q: string
  status: string
  flag: FlagFilter
  decision: string
  sort: SortKey
}

const VIEW_KEY = 'admin-subscribers-view-v1'
const DEFAULT_VIEW: ViewState = { q: '', status: 'all', flag: 'all', decision: 'all', sort: 'attention' }

function loadView(): ViewState {
  try {
    const raw = localStorage.getItem(VIEW_KEY)
    return raw ? { ...DEFAULT_VIEW, ...(JSON.parse(raw) as Partial<ViewState>) } : DEFAULT_VIEW
  } catch {
    return DEFAULT_VIEW
  }
}

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'attention', label: 'Needs me first' },
  { value: 'name', label: 'Name' },
  { value: 'monthly', label: 'Monthly total' },
  { value: 'open', label: 'Open balance' },
  { value: 'next', label: 'Renewal / trial end' },
]

const STATUSES = ['active', 'trial', 'expired', 'cancelled', 'suspended'] as const

function statusBucket(s: string): string {
  return s === 'trialing' ? 'trial' : s
}

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

const selectCls =
  'h-9 rounded border border-navy-border bg-navy-card px-2 text-sm text-ink-primary focus:border-gold focus:outline-none'

function RowDecision({
  row,
  persistence,
  onExtendTrial,
  selectRef,
}: {
  row: SubscriberRow
  persistence: 'rpc' | 'local'
  onExtendTrial: () => void
  selectRef?: (el: HTMLSelectElement | null) => void
}) {
  const save = useSaveDecision(persistence)
  return (
    <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
      <select
        ref={selectRef}
        value={row.decision}
        disabled={save.isPending}
        aria-label={`Decision for ${row.org_name}`}
        data-testid="row-decision"
        onChange={(e) => {
          const decision = e.target.value as SubscriberDecision
          if (decision === 'extend_trial') {
            onExtendTrial()
            return
          }
          save.mutate({ orgId: row.organization_id, decision, note: row.decision_note })
        }}
        className={`h-7 w-full rounded border bg-navy-card px-1.5 text-xs focus:border-gold focus:outline-none ${
          row.decision === 'none' ? 'border-navy-border text-ink-muted' : 'border-gold/40 text-ink-primary'
        }`}
      >
        {DECISION_OPTIONS.map((o) => (
          <option key={o.value} value={o.value} disabled={o.value === 'extend_trial' && statusBucket(row.status) !== 'trial'}>
            {o.value === 'none' ? '— decide —' : o.label}
          </option>
        ))}
      </select>
      {row.decided_at && (
        <div className="num mt-0.5 truncate text-[10px] text-ink-muted" title={row.decision_note || undefined}>
          {ddmmyyyy(row.decided_at)}{row.decided_by ? ` · ${row.decided_by.split('@')[0]}` : ''}
        </div>
      )}
    </div>
  )
}

function FlagChips({ row, max = 2 }: { row: SubscriberRow; max?: number }) {
  if (!row.flags.length) return <span className="text-[11px] text-ink-muted">All good</span>
  const list = sortedFlags(row.flags)
  return (
    <div className="flex flex-wrap gap-1">
      {list.slice(0, max).map((f) => <Chip key={f} tone={subscriberFlagTone(f, row)}>{subscriberFlagLabel(f, row)}</Chip>)}
      {list.length > max && (
        <Chip tone="gray" title={list.slice(max).map((f) => subscriberFlagLabel(f, row)).join(', ')}>+{list.length - max}</Chip>
      )}
    </div>
  )
}

function DueCell({ row }: { row: SubscriberRow }) {
  const k = keyDate(row)
  const d = daysUntil(k.at)
  if (d == null) return <span className="text-xs text-ink-muted">—</span>
  const tone = d < 0 ? 'text-danger' : d <= 7 ? 'text-warning' : d <= 14 ? 'text-ink-primary' : 'text-ink-secondary'
  const rel = d < 0 ? `${-d}d ago` : d === 0 ? 'today' : `in ${d}d`
  return (
    <div className="num leading-tight">
      <div className={`text-xs font-medium ${tone}`}>{rel}</div>
      <div className="text-[10px] text-ink-muted">{k.kind === 'trial_end' ? 'trial ends' : 'renews'} {ddmmyyyy(k.at)}</div>
    </div>
  )
}

function RowDetail({ row }: { row: SubscriberRow }) {
  const item = (k: string, v: React.ReactNode) => (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink-muted">{k}</div>
      <div className="num text-xs text-ink-primary">{v}</div>
    </div>
  )
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-3 md:grid-cols-4" data-testid="row-detail">
      {item('Owner', <bdi>{row.owner_name || row.owner_email || '—'}</bdi>)}
      {item('Monthly', moneyBreakdown(row))}
      {item('Card', !row.billing_linked ? 'Not linked to billing' : row.card_last4 ? `•••• ${row.card_last4}` : 'No card')}
      {item('Next charge', ddmmyyyy(row.next_charge_at))}
      {item('Trial end', ddmmyyyy(row.trial_end))}
      {item('Last charge', row.last_charge_at ? `${ddmmyyyy(row.last_charge_at)} · ${nis(row.last_charge_amount_agorot)}` : '—')}
      {item('First charge', `${row.first_charge_status ?? '—'} · ${periodLabel(row.first_charge_period_key)}`)}
      {item('WhatsApp', row.wa_status ?? '—')}
      {row.benefit_label && item('Benefit', <bdi>{row.benefit_label}</bdi>)}
      {row.decision_note && item('Note', <bdi>{row.decision_note}</bdi>)}
    </div>
  )
}

export default function SubscribersPage() {
  const [view, setView] = useState<ViewState>(loadView)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [panelId, setPanelId] = useState<string | null>(null)
  const [extendOrgId, setExtendOrgId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [showKeys, setShowKeys] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const panelDecisionRef = useRef<HTMLButtonElement>(null)
  const rowDecisionRefs = useRef(new Map<string, HTMLSelectElement>())

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, JSON.stringify(view))
    } catch {
      /* storage unavailable — view resets on reload */
    }
  }, [view])
  const patch = (p: Partial<ViewState>) => setView((v) => ({ ...v, ...p }))

  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['subscribers-control-center'],
    queryFn: subscribersApi.list,
    staleTime: 30_000,
  })
  const persistence = data?.persistence ?? 'local'
  const allRows = useMemo(() => (data?.rows ?? []).map(withDerivedFlags), [data?.rows])

  // Everything except the flag filter — the summary strip counts over this set,
  // so clicking a count shows exactly that many rows.
  const base = useMemo(() => {
    const needle = view.q.trim().toLowerCase()
    return allRows.filter((row) => {
      if (view.status !== 'all' && statusBucket(row.status) !== view.status) return false
      if (view.decision !== 'all' && row.decision !== view.decision) return false
      if (!needle) return true
      return (
        row.org_name.toLowerCase().includes(needle)
        || (row.owner_name ?? '').toLowerCase().includes(needle)
        || (row.owner_email ?? '').toLowerCase().includes(needle)
        || row.plan_name.toLowerCase().includes(needle)
        || row.plan_family.toLowerCase().includes(needle)
      )
    })
  }, [allRows, view.q, view.status, view.decision])

  const flagCounts = useMemo(() => {
    const c = Object.fromEntries(FLAG_ORDER.map((f) => [f, 0])) as Record<SubscriberFlag, number>
    let ok = 0
    let paying = 0
    for (const r of base) {
      if (!r.flags.length) ok += 1
      if (isPayingActive(r)) paying += 1
      for (const f of r.flags) c[f] += 1
    }
    return { c, ok, paying }
  }, [base])

  const statusCounts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const r of allRows) c[statusBucket(r.status)] = (c[statusBucket(r.status)] ?? 0) + 1
    return c
  }, [allRows])

  const trialCardCounts = useMemo(() => {
    const needle = view.q.trim().toLowerCase()
    let card = 0
    let noCard = 0
    let notLinked = 0
    for (const row of allRows) {
      if (statusBucket(row.status) !== 'trial') continue
      if (view.decision !== 'all' && row.decision !== view.decision) continue
      if (needle && !(
        row.org_name.toLowerCase().includes(needle)
        || (row.owner_name ?? '').toLowerCase().includes(needle)
        || (row.owner_email ?? '').toLowerCase().includes(needle)
        || row.plan_name.toLowerCase().includes(needle)
        || row.plan_family.toLowerCase().includes(needle)
      )) continue
      if (!row.billing_linked) notLinked += 1
      else if (row.card_last4) card += 1
      else noCard += 1
    }
    return { total: card + noCard + notLinked, card, noCard, notLinked }
  }, [allRows, view.q, view.decision])

  const mrr = useMemo(() => {
    let net = 0
    let vat = 0
    let gross = 0
    let n = 0
    for (const r of allRows) {
      if (!isPayingActive(r)) continue
      net += r.net_agorot
      vat += r.vat_agorot
      gross += r.gross_agorot
      n += 1
    }
    return { net, vat, gross, n }
  }, [allRows])

  const openTotal = useMemo(() => allRows.reduce((t, r) => t + r.open_balance_agorot, 0), [allRows])

  const rows = useMemo(() => {
    const list = base.filter((r) =>
      view.flag === 'all'
        ? true
        : view.flag === 'ok'
          ? r.flags.length === 0
          : view.flag === 'paying'
            ? isPayingActive(r)
            : view.flag === 'trial_missing_card'
              ? r.billing_linked && !r.card_last4
              : r.flags.includes(view.flag))
    const byName = (a: SubscriberRow, b: SubscriberRow) => a.org_name.localeCompare(b.org_name, 'he')
    const t = (s: string | null) => (s ? new Date(s).getTime() : Number.POSITIVE_INFINITY)
    list.sort((a, b) => {
      switch (view.sort) {
        case 'name': return byName(a, b)
        case 'monthly': return b.gross_agorot - a.gross_agorot || byName(a, b)
        case 'open': return b.open_balance_agorot - a.open_balance_agorot || byName(a, b)
        case 'next': return t(keyDate(a).at) - t(keyDate(b).at) || byName(a, b)
        default:
          return attentionScore(a) - attentionScore(b)
            || b.open_balance_agorot - a.open_balance_agorot
            || byName(a, b)
      }
    })
    return list
  }, [base, view.flag, view.sort])

  const panelRow = useMemo(() => allRows.find((r) => r.organization_id === panelId) ?? null, [allRows, panelId])

  const activeIndex = rows.findIndex((r) => r.organization_id === activeId)

  const move = useCallback((delta: number) => {
    if (!rows.length) return
    const next = rows[Math.max(0, Math.min(rows.length - 1, (activeIndex < 0 ? (delta > 0 ? -1 : rows.length) : activeIndex) + delta))]
    setActiveId(next.organization_id)
    setPanelId((p) => (p ? next.organization_id : p))
    document.querySelector(`[data-row-id="${next.organization_id}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [rows, activeIndex])

  const quickSave = useSaveDecision(persistence)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isTypingTarget(e.target)) (e.target as HTMLElement).blur()
        else if (panelId) { setPanelId(null); setExtendOrgId(null) }
        return
      }
      if (isTypingTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey) return
      const active = rows[activeIndex]
      switch (e.key) {
        case 'j': case 'ArrowDown': e.preventDefault(); move(1); break
        case 'k': case 'ArrowUp': e.preventDefault(); move(-1); break
        case 'Enter':
          if (active) { e.preventDefault(); setPanelId(active.organization_id) }
          break
        case 'd':
          e.preventDefault()
          if (panelId && panelDecisionRef.current) panelDecisionRef.current.focus()
          else if (active) rowDecisionRefs.current.get(active.organization_id)?.focus()
          break
        case 'x': case ' ':
          if (active) {
            e.preventDefault()
            setExpanded((s) => {
              const n = new Set(s)
              if (n.has(active.organization_id)) n.delete(active.organization_id)
              else n.add(active.organization_id)
              return n
            })
          }
          break
        case '/': e.preventDefault(); searchRef.current?.focus(); break
        case '?': setShowKeys((v) => !v); break
        default: {
          const opt = DECISION_OPTIONS.find((o) => o.key === e.key)
          if (opt && active && (opt.value === 'extend_trial' || active.decision !== opt.value)) {
            e.preventDefault()
            if (opt.value === 'extend_trial') {
              setPanelId(active.organization_id)
              setExtendOrgId(active.organization_id)
            } else {
              quickSave.mutate({ orgId: active.organization_id, decision: opt.value, note: active.decision_note })
            }
          }
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [rows, activeIndex, move, panelId, quickSave])

  const toggleExpand = (id: string) =>
    setExpanded((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  const openRow = (id: string) => {
    setActiveId(id)
    setPanelId(id)
    setExtendOrgId(null)
  }

  const openExtendTrial = (id: string) => {
    setActiveId(id)
    setPanelId(id)
    setExtendOrgId(id)
  }

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <CreditCard className="h-6 w-6 text-gold" />
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Subscribers</h1>
          <p className="mt-0.5 text-sm text-ink-secondary">What needs a licensing or billing decision today</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setShowKeys((v) => !v)}>
          <Keyboard size={14} className="mr-1" /> Shortcuts
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()} loading={isFetching && !isLoading}>
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="space-y-5">
        {header}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-navy-card" />)}
        </div>
        <div className="space-y-1">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded bg-navy-card" />)}
        </div>
        <div className="text-sm text-ink-secondary">Loading subscribers, billing periods and cards… (≈60 orgs, can take a few seconds)</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-5">
        {header}
        <div className="rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger" role="alert">
          <div className="font-medium">Could not load subscribers</div>
          <div className="mt-1 text-danger/80">{(error as Error).message || 'Unknown error'}</div>
          <Button type="button" size="sm" variant="secondary" className="mt-3" onClick={() => void refetch()}>Try again</Button>
        </div>
      </div>
    )
  }

  const flagPill = (key: FlagFilter, label: string, count: number, tone: keyof typeof TONE_CLASS) => {
    const on = view.flag === key
    return (
      <button
        key={key}
        type="button"
        data-testid={`summary-${key}`}
        onClick={() => patch({ flag: on ? 'all' : key })}
        disabled={count === 0 && !on}
        className={`flex min-w-[92px] flex-col items-start rounded-lg border px-3 py-2 text-[11px] transition disabled:cursor-default disabled:opacity-40 ${
          on ? 'ring-2 ring-gold' : 'hover:brightness-125'
        } ${count > 0 ? TONE_CLASS[tone] : TONE_CLASS.gray}`}
      >
        <span className="num text-xl font-bold leading-tight">{count}</span>
        <span>{label}</span>
      </button>
    )
  }

  const filtersActive = view.q || view.status !== 'all' || view.flag !== 'all' || view.decision !== 'all'
  const showCardColumn = view.flag === 'paying' || view.status === 'trial'

  return (
    <div className="space-y-4">
      {header}

      {persistence === 'local' && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-2 text-xs text-warning">
          Decisions are saved on this browser only until migration <code>20260920150000_admin_subscriber_decisions.sql</code> is applied. Money and flags read live tables.
        </div>
      )}

      {/* Triage strip */}
      <div className="grid gap-4 rounded-lg border border-navy-border bg-navy-card p-4 lg:grid-cols-[1fr_auto]" data-testid="summary-strip">
        <div>
          <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-ink-muted">Needs me today</div>
          <div className="flex flex-wrap gap-2">
            {FLAG_ORDER.map((f) => flagPill(f, FLAG_LABEL[f], flagCounts.c[f], FLAG_TONE[f]))}
            {flagPill('ok', 'All good', flagCounts.ok, 'green')}
            {flagPill('paying', 'Paying', flagCounts.paying, 'green')}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
            {STATUSES.map((s) => {
              const on = view.status === s
              if (s === 'trial') {
                const noCardOn = on && view.flag === 'trial_missing_card'
                return (
                  <div key={s} className="flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid="status-trial"
                      onClick={() => patch({ status: on ? 'all' : 'trial', flag: view.flag === 'trial_missing_card' ? 'all' : view.flag })}
                      className={`rounded border border-navy-border px-2 py-0.5 text-ink-secondary ${on && !noCardOn ? 'ring-2 ring-gold' : 'hover:bg-navy-hover'}`}
                    >
                      <b className="num text-ink-primary">{trialCardCounts.total}</b> Trial
                    </button>
                    <span className="text-ink-muted">·</span>
                    <span><b className="num text-success">{trialCardCounts.card}</b> card</span>
                    <span className="text-ink-muted">·</span>
                    <button
                      type="button"
                      data-testid="trial-no-card"
                      disabled={trialCardCounts.noCard === 0 && !noCardOn}
                      onClick={() => patch({ status: 'trial', flag: noCardOn ? 'all' : 'trial_missing_card' })}
                      className={`rounded px-1 py-0.5 text-warning disabled:cursor-default disabled:opacity-40 ${noCardOn ? 'ring-2 ring-gold' : 'hover:bg-navy-hover'}`}
                    >
                      <b className="num">{trialCardCounts.noCard}</b> no card
                    </button>
                    <span className="text-ink-muted">·</span>
                    <span><b className="num text-ink-muted">{trialCardCounts.notLinked}</b> not linked</span>
                  </div>
                )
              }
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`status-${s}`}
                  onClick={() => patch({ status: on ? 'all' : s, flag: view.flag === 'trial_missing_card' ? 'all' : view.flag })}
                  className={`rounded border border-navy-border px-2 py-0.5 capitalize text-ink-secondary ${on ? 'ring-2 ring-gold' : 'hover:bg-navy-hover'}`}
                >
                  <b className="num text-ink-primary">{statusCounts[s] ?? 0}</b> {s}
                </button>
              )
            })}
          </div>
        </div>
        <div className="num min-w-[240px] border-navy-border lg:border-l lg:pl-5" data-testid="summary-mrr">
          <button
            type="button"
            onClick={() => patch({ flag: view.flag === 'paying' ? 'all' : 'paying' })}
            className="mb-1 text-[10px] font-medium uppercase tracking-wide text-ink-muted transition hover:text-gold"
          >
            MRR · {mrr.n} paying
          </button>
          <div className="text-2xl font-bold text-ink-primary">{nis(mrr.gross)}</div>
          <div className="mt-1 flex justify-between text-xs text-ink-secondary"><span>Net</span><span>{nis(mrr.net)}</span></div>
          <div className="flex justify-between text-xs text-ink-secondary"><span>+ VAT</span><span>{nis(mrr.vat)}</span></div>
          <div className="flex justify-between text-xs text-ink-secondary"><span>Open balance</span><span className={openTotal > 0 ? 'font-semibold text-danger' : ''}>{nis(openTotal)}</span></div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            ref={searchRef}
            value={view.q}
            onChange={(e) => patch({ q: e.target.value })}
            placeholder="Search org, owner, plan  ( / )"
            aria-label="Search"
            className="h-9 w-full rounded border border-navy-border bg-navy-card pl-8 pr-2 text-sm text-ink-primary placeholder:text-ink-muted focus:border-gold focus:outline-none"
          />
        </div>
        <select aria-label="Decision filter" value={view.decision} onChange={(e) => patch({ decision: e.target.value })} className={selectCls}>
          <option value="all">Any decision</option>
          {DECISION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select aria-label="Sort" value={view.sort} onChange={(e) => patch({ sort: e.target.value as SortKey })} className={selectCls}>
          {SORTS.map((s) => <option key={s.value} value={s.value}>Sort: {s.label}</option>)}
        </select>
        {filtersActive && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setView((v) => ({ ...DEFAULT_VIEW, sort: v.sort }))}>Clear filters</Button>
        )}
        <span className="text-xs text-ink-muted" data-testid="shown-count">{rows.length} of {allRows.length} shown</span>
      </div>

      {showKeys && (
        <div className="rounded-lg border border-navy-border bg-navy-card px-4 py-3 text-xs text-ink-secondary" data-testid="shortcuts">
          <b className="text-ink-primary">Keyboard:</b>{' '}
          <kbd>j</kbd>/<kbd>↓</kbd> next · <kbd>k</kbd>/<kbd>↑</kbd> previous · <kbd>Enter</kbd> open panel · <kbd>Esc</kbd> close ·{' '}
          <kbd>d</kbd> focus decision · <kbd>0</kbd>–<kbd>6</kbd> set decision ({DECISION_OPTIONS.map((o) => `${o.key}=${o.label}`).join(', ')}) ·{' '}
          <kbd>x</kbd>/<kbd>Space</kbd> expand row · <kbd>/</kbd> search · <kbd>Ctrl+Enter</kbd> save note · <kbd>?</kbd> this help
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-navy-border bg-navy-card px-6 py-10 text-center" data-testid="empty">
          <div className="text-sm text-ink-primary">
            {(allRows.length) === 0 ? 'No JumpStart subscribers found.' : 'No subscriber matches these filters.'}
          </div>
          {filtersActive && (
            <Button type="button" size="sm" variant="secondary" className="mt-3" onClick={() => setView((v) => ({ ...DEFAULT_VIEW, sort: v.sort }))}>
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-navy-border bg-navy-card md:block">
            <table className="w-full table-fixed text-sm" data-testid="subscribers-table">
              <colgroup>
                <col className="w-7" />
                <col />
                <col className="w-[104px]" />
                <col className="w-[84px]" />
                <col className="w-[112px]" />
                <col className={showCardColumn ? 'w-[76px]' : 'hidden w-[76px] min-[1400px]:table-column'} />
                <col className="w-[88px]" />
                <col className="w-[118px]" />
                <col className="w-[190px]" />
                <col className="w-[136px]" />
              </colgroup>
              <thead>
                <tr className="border-b border-navy-border text-left text-[10px] uppercase tracking-wide text-ink-muted">
                  <th />
                  <th className="px-2 py-2 font-medium">Name</th>
                  <th className="px-2 py-2 font-medium">Plan</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 text-right font-medium">Monthly <span className="normal-case">(incl. VAT)</span></th>
                  <th className={showCardColumn ? 'px-2 py-2 font-medium' : 'hidden px-2 py-2 font-medium min-[1400px]:table-cell'}>Card</th>
                  <th className="px-2 py-2 text-right font-medium">Open</th>
                  <th className="px-2 py-2 font-medium">Renews / trial ends</th>
                  <th className="px-2 py-2 font-medium">Flags</th>
                  <th className="px-2 py-2 font-medium">Decision</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const id = row.organization_id
                  const quiet = row.flags.length === 0
                  const isActive = id === activeId
                  const isOpen = expanded.has(id)
                  const score = attentionScore(row)
                  return (
                    <Fragment key={id}>
                      <tr
                        data-row-id={id}
                        data-org-name={row.org_name}
                        aria-selected={isActive}
                        onClick={() => openRow(id)}
                        className={`cursor-pointer border-b border-navy-border/50 border-l-2 ${
                          score === 0 ? 'border-l-danger' : score === 1 ? 'border-l-warning' : 'border-l-transparent'
                        } ${isActive ? 'bg-navy-hover' : 'hover:bg-navy-hover/60'} ${quiet && !isActive ? 'text-ink-secondary' : 'text-ink-primary'}`}
                      >
                        <td className="pl-1">
                          <button
                            type="button"
                            aria-label={isOpen ? 'Collapse row' : 'Expand row'}
                            onClick={(e) => { e.stopPropagation(); toggleExpand(id) }}
                            className="rounded p-0.5 text-ink-muted hover:text-ink-primary"
                          >
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </td>
                        <td className="truncate px-2 py-1.5">
                          <span dir="auto" className={quiet ? '' : 'font-medium'} title={row.owner_name || row.owner_email || undefined}>{row.org_name}</span>
                        </td>
                        <td className="truncate px-2 py-1.5 text-xs"><bdi title={row.plan_code}>{row.plan_family}</bdi></td>
                        <td className="px-2 py-1.5"><Chip tone={STATUS_TONE[row.status] ?? 'gray'}>{row.status}</Chip></td>
                        <td className="num px-2 py-1.5 text-right" title={moneyBreakdown(row)} data-money>
                          {row.gross_agorot ? (
                            <>
                              <div className="leading-tight">{nis(row.gross_agorot)}</div>
                              <div className="text-[10px] leading-tight text-ink-muted">{nis(row.net_agorot)} + VAT</div>
                            </>
                          ) : <span className="text-ink-muted">—</span>}
                        </td>
                        <td className={showCardColumn ? 'num px-2 py-1.5 text-xs' : 'num hidden px-2 py-1.5 text-xs min-[1400px]:table-cell'}>
                          {!row.billing_linked
                            ? <span className="text-ink-muted" title="Not linked to billing">not linked</span>
                            : row.card_last4
                              ? <span className={statusBucket(row.status) === 'trial' ? 'text-success' : ''}>•••• {row.card_last4}</span>
                              : statusBucket(row.status) === 'trial'
                                ? <span className="text-warning">no card</span>
                                : <span className={isPayingActive(row) ? 'text-danger' : 'text-ink-muted'}>none</span>}
                        </td>
                        <td className={`num px-2 py-1.5 text-right text-xs ${row.open_balance_agorot > 0 ? 'font-medium text-danger' : 'text-ink-muted'}`}>
                          {row.open_balance_agorot > 0 ? nis(row.open_balance_agorot) : '—'}
                        </td>
                        <td className="px-2 py-1.5"><DueCell row={row} /></td>
                        <td className="px-2 py-1.5"><FlagChips row={row} /></td>
                        <td className="px-2 py-1">
                          <RowDecision
                            row={row}
                            persistence={persistence}
                            onExtendTrial={() => openExtendTrial(id)}
                            selectRef={(el) => {
                              if (el) rowDecisionRefs.current.set(id, el)
                              else rowDecisionRefs.current.delete(id)
                            }}
                          />
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-navy-border/50 bg-navy-deeper/60">
                          <td />
                          <td colSpan={9}><RowDetail row={row} /></td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden" data-testid="subscribers-cards">
            {rows.map((row) => (
              <button
                key={row.organization_id}
                type="button"
                onClick={() => openRow(row.organization_id)}
                className={`block w-full rounded-lg border border-navy-border bg-navy-card p-3 text-left ${
                  attentionScore(row) === 0 ? 'border-l-2 border-l-danger' : attentionScore(row) === 1 ? 'border-l-2 border-l-warning' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink-primary" dir="auto">{row.org_name}</div>
                    <div className="text-xs text-ink-secondary"><bdi>{row.plan_family}</bdi> · {row.status}</div>
                  </div>
                  <div className="num text-right text-sm text-ink-primary">
                    {row.gross_agorot ? nis(row.gross_agorot) : '—'}
                    {row.gross_agorot ? <div className="text-[10px] text-ink-muted">{nis(row.net_agorot)} + VAT</div> : null}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <FlagChips row={row} max={3} />
                  <span className="text-[11px] text-ink-muted">{row.decision !== 'none' ? DECISION_LABEL[row.decision] : ''}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {panelRow && (
        <>
          <div className="fixed inset-0 z-30 bg-black/40 md:bg-black/20" onClick={() => { setPanelId(null); setExtendOrgId(null) }} aria-hidden="true" />
          <SubscriberPanel
            ref={panelDecisionRef}
            row={panelRow}
            persistence={persistence}
            startExtendTrial={extendOrgId === panelRow.organization_id}
            onClose={() => { setPanelId(null); setExtendOrgId(null) }}
          />
        </>
      )}

      <div className="text-[11px] text-ink-muted">
        Loaded {dataUpdatedAt ? ddmmyyyyTime(new Date(dataUpdatedAt).toISOString()) : '—'} · press <kbd>?</kbd> for keyboard shortcuts
      </div>
    </div>
  )
}
